import { db } from '@app/icare/client/db'
import {
  localPaginatedQueryOptions,
  type ResultTypeFromQueryResult,
} from '@app/icare/client/db/client/localQueryOptions'
import { FormStatusBadge } from '@app/icare/components/status/FormStatusBadge'
import { queryKit } from '@app/icare/db/query/queryKit'
import { PatientOrderFormType, PatientOrderFormTypeEnum } from '@app/icare/db/schema/form/PatientFormType'
import {
  type PatientIdType,
  type PatientStatus,
  PatientStatusEnum,
  type PatientStatusType,
} from '@app/icare/entities/patient/schema/Patient'
import {
  OrderFormStatusEnum,
  type OrderFormStatusType,
  SupplyEnum,
  type SupplyType,
} from '@app/icare/entities/patientForm/schema/PatientForm'
import ActiveEditors from '@app/icare/entities/user/components/ActiveEditors'
import { UserInfo } from '@app/icare/entities/user/components/UserInfo'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { createQueryStore, F, Q, QueryBaseSchema, useColumnDef } from '@pkg/api/query'
import { formatDate, formatDateTime } from '@pkg/shared/utils'
import { ButtonLink, FilterButton } from '@pkg/ui/button'
import { LinkIndicatorIcon, SearchInput } from '@pkg/ui/common'
import { FormattedPatientNameAndAddress } from '@pkg/ui/formattedContent'
import { usePageContainerOverride } from '@pkg/ui/layout/PageContainer'
import { QueryFilterWrapper } from '@pkg/ui/query/QueryFilterWrapper'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Descriptions, Divider, Space, Table } from 'antd'
import { sql } from 'kysely'
import { jsonObjectFrom } from 'kysely/helpers/sqlite'
import { useState } from 'react'
import { z } from 'zod'
import { useShallow } from 'zustand/react/shallow'

export const Route = createFileRoute('/_app/_dashboards/orders')({
  component: RouteComponent,
})

const FilterSchema = QueryBaseSchema.extend({
  sortBy: z
    .enum([
      'patient.personal.lastName',
      'patientForm.status',
      'patientForm.type',
      'patientForm.form.supply',
      'patientForm.form.deliveryTime.from',
    ])
    .default('patientForm.form.deliveryTime.from')
    .nullish(),
  filters: z
    .object({
      'patientForm.status': Q.in(z.enum(OrderFormStatusEnum.$.zodEnum())).nullish(),
      'patient.status': Q.in(z.enum(PatientStatusEnum.$.zodEnum())).nullish(),
      'patientForm.type': Q.in(z.enum(PatientOrderFormTypeEnum.$.zodEnum())).nullish(),
      'patientForm.form.supply': Q.in(z.enum(SupplyEnum.$.zodEnum())).nullish(),
    })
    .optional(),
})
const PatientStatusValueSchema = z.enum(PatientStatusEnum.$.zodEnum())
const INITIAL_FILTERS = {
  'patientForm.status': F.in([
    OrderFormStatusEnum.new,
    OrderFormStatusEnum.processing,
    OrderFormStatusEnum.delivered_to_stock,
    OrderFormStatusEnum.resend_recipe_request,
  ]),
  'patient.status': F.in([PatientStatusEnum.new, PatientStatusEnum.open]),
}
const useOrderQueryStore = createQueryStore({
  schema: FilterSchema,
  initialState: {
    filters: INITIAL_FILTERS,
    sortBy: 'patientForm.form.deliveryTime.from',
    sortDirection: 'ascend',
  },
})

function RouteComponent() {
  const queryStore = useOrderQueryStore(useShallow(s => s))
  const [open, setOpen] = useState(false)

  const ordersParams = queryKit.parseQuery(queryStore, { paginationMode: 'offset' })
  const patientStatusFilter = ordersParams.filters.find(f => f.field === 'patient.status')
  const patientStatusValues: PatientStatusType[] =
    patientStatusFilter?.operator === 'in' && Array.isArray(patientStatusFilter.value)
      ? patientStatusFilter.value.flatMap(value => {
          const parsed = PatientStatusValueSchema.safeParse(value)
          return parsed.success ? [parsed.data] : []
        })
      : patientStatusFilter?.operator === 'eq'
        ? (() => {
            const parsed = PatientStatusValueSchema.safeParse(patientStatusFilter.value)
            return parsed.success ? [parsed.data] : []
          })()
        : []

  const supplyFilter = ordersParams.filters.find(f => f.field === 'patientForm.form.supply')
  const supplyValues: SupplyType[] =
    supplyFilter?.operator === 'in' && Array.isArray(supplyFilter.value)
      ? supplyFilter.value.flatMap(value => (SupplyEnum.$.is(value) ? [value] : []))
      : supplyFilter?.operator === 'eq' && SupplyEnum.$.is(supplyFilter.value)
        ? [supplyFilter.value]
        : []

  const { data, isFetching } = useQuery(
    localPaginatedQueryOptions({
      query: db
        .selectFrom('patientForm')
        .selectAll('patientForm')
        .select(sb =>
          jsonObjectFrom(
            sb
              .selectFrom('patient')
              .select(['id', 'personal', 'contact', 'status', 'address'])
              .whereRef('patient.id', '=', 'patientForm.patientId'),
          ).as('patient'),
        )
        .where('patientForm.type', 'in', Object.values(PatientOrderFormType))
        .$if(patientStatusValues.length > 0, qb =>
          qb.where(({ exists, selectFrom }) =>
            exists(
              selectFrom('patient')
                .select('patient.id')
                .whereRef('patient.id', '=', 'patientForm.patientId')
                .where(eb => eb(eb.ref('patient.status', '->>').key('value'), 'in', patientStatusValues)),
            ),
          ),
        )
        .$if(supplyValues.length > 0, qb =>
          qb.where(sql`json_extract("patientForm"."form", '$.supply')`, 'in', supplyValues),
        )
        .$call(queryKit.deleted(ordersParams.deleted, db.dynamic.ref('patientForm.deletedAt')))
        .$call(queryKit.filters(ordersParams, ['patientForm.status', 'status', 'patientForm.type']))
        .$call(queryKit.search(ordersParams))
        .$if(ordersParams.sortBy === 'patientForm.form.supply', qb =>
          qb.orderBy(sql`json_extract("patientForm"."form", '$.supply')`, ordersParams.sortDir),
        )
        .$if(ordersParams.sortBy === 'patientForm.form.deliveryTime.from', qb =>
          qb.orderBy(sql`json_extract("patientForm"."form", '$.deliveryTime.from')`, ordersParams.sortDir),
        )
        .$if(ordersParams.sortBy === 'patient.personal.lastName', qb =>
          qb.orderBy(
            sql`(
              select json_extract("patient"."personal", '$.lastName')
              from "patient"
              where "patient"."id" = "patientForm"."patientId"
            )`,
            ordersParams.sortDir,
          ),
        )
        .$if(
          ordersParams.sortBy !== 'patientForm.form.supply' &&
            ordersParams.sortBy !== 'patientForm.form.deliveryTime.from' &&
            ordersParams.sortBy !== 'patient.personal.lastName',
          qb => qb.$call(queryKit.sort(ordersParams)),
        )
        .$call(queryKit.paginate(ordersParams)),
      params: ordersParams,
    }),
  )

  type OrderRecord = ResultTypeFromQueryResult<typeof data>

  const columns = useColumnDef<OrderRecord>(
    () => [
      {
        key: 'patient.personal.lastName',
        title: <Trans>Patientenakte</Trans>,
        fixed: 'start',
        sorter: true,
        render: (_, record) =>
          record.patient && (
            <FormattedPatientNameAndAddress
              patient={{
                ...record.patient,
                id: record.patient.id as PatientIdType,
                status: record.patient.status as PatientStatus,
              }}
            />
          ),
      },
      {
        key: 'patientForm.status',
        title: t`Status`,
        sorter: true,
        render: (_, record) => (
          <FormStatusBadge<OrderFormStatusType>
            value={record.status as OrderFormStatusType}
            statusEnum={OrderFormStatusEnum}
          />
        ),
      },
      {
        key: 'patientForm.form.deliveryTime.from',
        title: <Trans>Späteste Lieferung</Trans>,
        sorter: true,
        render: (_, record) =>
          'deliveryTime' in record.form && record.form.deliveryTime ? formatDate(record.form.deliveryTime.from) : '',
      },
      {
        key: 'patientForm.type',
        title: t`Therapie`,
        sorter: true,
        render: (_, record) =>
          PatientOrderFormTypeEnum.$.is(record.type)
            ? PatientOrderFormTypeEnum.$.getLabel(record.type, { variant: 'short' })
            : '',
      },
      {
        key: 'patientForm.form.supply',
        title: <Trans>Versorgungsart</Trans>,
        sorter: true,
        render: (_, record) => ('supply' in record.form ? SupplyEnum.$.getLabel(record.form.supply) : ''),
      },
      {
        key: 'usersEditing',
        title: <Trans>aktuelle Bearbeitende</Trans>,
        sorter: false,
        render: (_, record) => (
          <ActiveEditors
            routeId="/_app/patienten/$id/_edit/_documents/_forms/bedarfsermittlung/$form"
            params={{ id: record.patientId, form: record.id }}
          />
        ),
      },
      {
        key: 'patientForm.id',
        title: t`Aktionen`,
        sorter: false,
        fixed: 'end',
        render: (_, record) => (
          <ButtonLink
            to="/patienten/$id/bedarfsermittlung/$form"
            params={{ id: record.patientId, form: record.id }}
            size="small"
          >
            <Trans>zum Dokument</Trans>
            <LinkIndicatorIcon />
          </ButtonLink>
        ),
      },
    ],
    [],
    useOrderQueryStore,
  )
  usePageContainerOverride(
    {
      header: {
        extra: (
          <Space size="small">
            <SearchInput
              size="small"
              value={queryStore.meta.searchRawValue}
              onChange={e => queryStore.setSearch(e.target.value)}
            />
            <FilterButton activeFilterCount={queryStore.meta.activeFilterCount} onClick={() => setOpen(true)} />
          </Space>
        ),
      },
    },
    [queryStore.meta.searchRawValue, queryStore.setSearch, queryStore.meta.activeFilterCount, setOpen, queryStore],
  )

  return (
    <QueryFilterWrapper
      queryStore={useOrderQueryStore}
      open={open}
      onOpenChange={setOpen}
      filterConfig={[
        {
          name: 'patient.status',
          label: <Trans>Status der Patientenakte</Trans>,
          mode: 'multiple',
          options: PatientStatusEnum.$.options,
        },
        {
          name: 'patientForm.status',
          label: <Trans>Status der Bedarfsermittlung</Trans>,
          mode: 'multiple',
          options: OrderFormStatusEnum.$.options,
        },
        {
          name: 'patientForm.form.supply',
          label: <Trans>Versorgungsart</Trans>,
          mode: 'multiple',
          options: SupplyEnum.$.options.map(option => ({
            value: option.value,
            label: SupplyEnum.$.getLabel(option.value),
          })),
        },
        {
          name: 'patientForm.type',
          label: <Trans>Therapie</Trans>,
          mode: 'multiple',
          options: PatientOrderFormTypeEnum.$.options.map(option => ({
            value: option.value,
            label: PatientOrderFormTypeEnum.$.getLabel(option.value, { variant: 'short' }),
          })),
        },
      ]}
    >
      <Table<OrderRecord>
        className="min-w-full"
        dataSource={data?.data ?? []}
        loading={isFetching}
        rowKey="id"
        pagination={data}
        columns={columns}
        size="small"
        scroll={{ x: 'max-content' }}
        onChange={queryStore.onChange}
        expandable={{
          expandedRowRender: record => (
            <Descriptions
              column={1}
              size="small"
              style={{ paddingLeft: 32 }}
              items={[
                {
                  label: t`Erstellt von`,
                  key: 'createdBy',
                  children: <UserInfo userId={record.createdBy} />,
                },
                {
                  label: t`Erstellt am`,
                  key: 'createdAt',
                  children: formatDateTime(record.createdAt),
                },
                {
                  label: t`Geändert von`,
                  key: 'updatedBy',
                  children: <UserInfo userId={record.updatedBy} />,
                },
                {
                  label: t`Geändert am`,
                  key: 'updatedAt',
                  children: formatDateTime(record.updatedAt),
                },
              ]}
            />
          ),
        }}
      />
    </QueryFilterWrapper>
  )
}
